/**
 * Offline scope resolution (`specs/offline-attendance-v1/implementation-plan.md`,
 * task FE-7; decisions D3, D4, D16).
 *
 * Every offline-attendance record on the device — the provisioned context, the
 * archived grants, the confirmed snapshot, the outbox, the receipts — belongs
 * to ONE scope: `{apiOrigin, companyKey, userId, installationId}`. Its
 * `scopeKey` (D3) is what the repository keys and seals rows by, so another
 * account, company, ERPat environment or installation can never list, open or
 * upload them:
 *
 *   scopeKey = base64url(SHA-256("syntry-offline-scope|v1|" + apiOrigin + "|" +
 *              companyKey + "|" + userId + "|" + installationId))   // 43 chars
 *
 * Where each part comes from:
 *   - `apiOrigin` — `new URL(API_BASE_URL).origin`, so punches captured against
 *     one ERPat environment can never reach another;
 *   - `userId` / `companyKey` — the ACCESS TOKEN, read here directly
 *     ({@link tokenIdentity}): `getSessionUserId()` drops a numeric `sub` and
 *     is deliberately left alone (D16 — changing it would re-key other modules'
 *     storage). The token's expiry is irrelevant: the offline surface outlives
 *     the access token (spec §3 point 4), and nothing here authorizes an API call;
 *   - `installationId` — the offline installation uuid in IndexedDB (D4),
 *     minted once by the repository.
 *
 * The employee id and the grant issuer are NOT part of the key (the employee id
 * is unknown before the first provisioning); they live in the sealed context
 * and are compared by the context service on every answer.
 *
 * Nothing is cached here: after the database is wiped (`onOfflineDbWiped`) the
 * orchestrator simply resolves again and gets the NEW installation id — and
 * therefore a new scope.
 */
import { erpatClient, getSessionEpoch, getStoredCompanyKey } from '@/services/api/erpat-client';
import { normalizeDecimalId } from '@/contracts/offline-attendance-v1';
import { normalizeTenant } from '@/services/session-scope';
import { getOrCreateInstallationId } from '@/services/storage/attendance-storage';
import type { OfflineScope } from '@/types/offline-attendance';
import { API_BASE_URL } from '@/utils/env';

/** D3's domain prefix; the parts follow it, joined by {@link SEPARATOR}. */
const SCOPE_KEY_PREFIX = 'syntry-offline-scope|v1|';
/** The part separator. A part containing it would let two scopes share one key. */
const SEPARATOR = '|';

/** The account the access token names. */
export interface TokenIdentity {
  /** Decimal string (the JWT `sub`, a number or a string). */
  userId: string;
  /** The tenant, normalized (`normalizeTenant()`). */
  companyKey: string;
}

/**
 * The account the cached access token names, or `null` when there is no token,
 * it does not decode, or it does not name a user and a company.
 *
 *  - `userId`: the `sub` claim as a positive decimal string — a number (`205`)
 *    or a string (`"205"`, trimmed like `getSessionUserId()`); anything else
 *    (missing, zero, fractional, `"0205"`, `"user-205"`) is no identity.
 *  - `companyKey`: the token's `company_key` claim when it holds more than
 *    whitespace, else the remembered company key (`getStoredCompanyKey()`) —
 *    normalized (trim + lower-case); none at all is no identity.
 *
 * The signature is NOT verified (ERPat verifies every request): this names whose
 * local records to open, it authorizes nothing.
 */
export function tokenIdentity(): TokenIdentity | null {
  let claims: unknown;
  try {
    claims = erpatClient.auth.decode();
  } catch {
    // No access token, or one that does not decode: no identity to scope by.
    return null;
  }
  if (typeof claims !== 'object' || claims === null) return null;

  const sub: unknown = Reflect.get(claims, 'sub');
  const userId = normalizeDecimalId(typeof sub === 'string' ? sub.trim() : sub);
  if (userId === null) return null;

  const claimedKey: unknown = Reflect.get(claims, 'company_key');
  const fromClaim = typeof claimedKey === 'string' ? normalizeTenant(claimedKey) : '';
  const companyKey = fromClaim !== '' ? fromClaim : normalizeTenant(getStoredCompanyKey());
  if (companyKey === '') return null;

  return { userId, companyKey };
}

/**
 * The D3 scope key: `base64url(SHA-256("syntry-offline-scope|v1|" + apiOrigin +
 * "|" + companyKey + "|" + userId + "|" + installationId))`, 43 characters, no
 * padding. Deterministic. Rejects with a `TypeError` when a part is empty or
 * contains the `|` separator (two different scopes could otherwise share a key),
 * and with WebCrypto's error when there is no `crypto.subtle` (an insecure origin).
 */
export async function deriveScopeKey(
  apiOrigin: string,
  companyKey: string,
  userId: string,
  installationId: string,
): Promise<string> {
  const parts = [apiOrigin, companyKey, userId, installationId];
  if (!parts.every(isScopePart)) {
    throw new TypeError('deriveScopeKey: every scope part must be a non-empty string without "|"');
  }
  const input = new TextEncoder().encode(SCOPE_KEY_PREFIX + parts.join(SEPARATOR));
  const digest = await crypto.subtle.digest('SHA-256', input);
  return base64Url(new Uint8Array(digest));
}

/**
 * The scope of the signed-in account on this installation, or `null` when there
 * is none to resolve: no readable identity ({@link tokenIdentity}), an API
 * origin or company key that cannot form a scope, or a session that ended or
 * changed account while the installation id was being read (the caller simply
 * resolves again for the new session).
 *
 * Without an identity it never touches IndexedDB. The installation id is read —
 * or minted on first use — through the repository, so a failing IndexedDB
 * rejects with the repository's `StorageError` (`unavailable`, `corrupt` for a
 * stored id that is not a uuid v4, …).
 */
export async function resolveScope(): Promise<OfflineScope | null> {
  const identity = tokenIdentity();
  if (identity === null) return null;
  const apiOrigin = apiOriginOf(API_BASE_URL);
  if (apiOrigin === null || !isScopePart(identity.companyKey)) return null;

  const epoch = getSessionEpoch();
  const installationId = await getOrCreateInstallationId();
  const scopeKey = await deriveScopeKey(apiOrigin, identity.companyKey, identity.userId, installationId);
  // Checked after the LAST await: a scope for a session that ended meanwhile is no scope.
  if (!stillSignedInAs(identity, epoch)) return null;

  return { apiOrigin, companyKey: identity.companyKey, userId: identity.userId, installationId, scopeKey };
}

/** True when `a` and `b` name the same scope (every field, the scope key included). */
export function sameOfflineScope(a: OfflineScope, b: OfflineScope): boolean {
  return (
    a.apiOrigin === b.apiOrigin &&
    a.companyKey === b.companyKey &&
    a.userId === b.userId &&
    a.installationId === b.installationId &&
    a.scopeKey === b.scopeKey
  );
}

/* ── Internals ──────────────────────────────────────────────────────────── */

function isScopePart(value: string): boolean {
  return value.length > 0 && !value.includes(SEPARATOR);
}

/** The origin of the configured ERPat base URL, or `null` when it has none (an opaque origin). */
function apiOriginOf(baseUrl: string): string | null {
  try {
    const { origin } = new URL(baseUrl);
    return origin !== 'null' && isScopePart(origin) ? origin : null;
  } catch {
    // Not a URL at all: no origin to bind records to.
    return null;
  }
}

/** The session that began the resolution is still the one signed in, as the same account. */
function stillSignedInAs(identity: TokenIdentity, epoch: number): boolean {
  if (getSessionEpoch() !== epoch) return false;
  const now = tokenIdentity();
  return now !== null && now.userId === identity.userId && now.companyKey === identity.companyKey;
}

/** base64url without padding (RFC 4648 §5). */
function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
